<template>
  <div class="ec-controlli">

    <div class="card">
      <div class="flex align-items-center justify-content-between flex-wrap ec-spazio-l mb-4">
        <div>
          <h5 class="mb-1">Controlli estratti conto</h5>
          <div class="text-500 text-sm">
            Confronta l'estratto conto ufficiale della banca con i movimenti registrati in ask.
            I documenti si caricano dalla pagina Estratti Conto.
          </div>
        </div>
        <div class="ec-comandi">
          <Dropdown v-model="contoFiltro" :options="contiFiltro" optionLabel="nome" optionValue="id"
            placeholder="Tutti i conti" class="ec-filtro" @change="caricaDocumenti" />
          <Button label="Aggiorna" icon="pi pi-refresh" class="p-button-outlined"
            :loading="caricamento" @click="caricaDocumenti" />
          <Button v-if="isAdmin" label="Registro attività" icon="pi pi-history"
            class="p-button-outlined p-button-secondary" @click="apriRegistro" />
        </div>
      </div>

      <DataTable :value="documenti" dataKey="id" responsiveLayout="scroll" class="ec-tabella"
        v-model:expandedRows="righeAperte" @rowExpand="onRowExpand"
        :loading="caricamento" :paginator="documenti.length > 15" :rows="15" :rowsPerPageOptions="[15, 25, 50, 100]"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Visualizzati {first}-{last} di {totalRecords}">

        <template #empty>
          <div class="ec-empty">
            Nessun estratto conto caricato. Caricalo dalla pagina Estratti Conto, dal tab della banca.
          </div>
        </template>

        <Column :expander="true" style="width:3rem" />

        <Column field="banca" header="Banca" style="width:9rem" bodyClass="ec-nowrap">
          <template #body="{ data }"><span class="font-semibold">{{ data.banca }}</span></template>
        </Column>

        <!-- Unica colonna elastica: e' quella che deve assorbire lo spazio. -->
        <Column field="nome_file" header="Documento" bodyClass="ec-desc-cell">
          <template #body="{ data }">
            <span class="ec-file" :title="data.nome_file">{{ data.nome_file }}</span>
            <span class="ec-sub">{{ formatBytes(data.dimensione) }}</span>
          </template>
        </Column>

        <Column header="Caricato il" style="width:11rem" bodyClass="ec-nowrap">
          <template #body="{ data }">{{ formatDateTime(data.caricato_il) }}</template>
        </Column>

        <Column header="Ultimo controllo" style="width:16rem">
          <template #body="{ data }">
            <span v-if="!data.ultimo_esito" class="text-500">Mai eseguito</span>
            <template v-else>
              <div class="ec-esiti">
                <span class="ec-pill" :class="pillClasse(data.ultimo_esito)">{{ pillTesto(data.ultimo_esito) }}</span>
                <span v-if="data.ultimo_esito.stato === 'ok' && data.ultimo_esito.quadratura_ok === false"
                  class="ec-pill ec-pill-warn">PDF non quadra</span>
              </div>
              <span class="ec-sub ec-nowrap">{{ formatDateTime(data.ultimo_esito.eseguita_il) }}</span>
            </template>
          </template>
        </Column>

        <Column header="Controlli" style="width:7rem" bodyClass="text-center" headerClass="ec-th-center">
          <template #body="{ data }"><span class="ec-count">{{ data.esecuzioni }}</span></template>
        </Column>

        <Column header="" style="width:14rem">
          <template #body="{ data }">
            <div class="ec-azioni">
              <Button label="Esegui" icon="pi pi-play" class="p-button-success"
                @click="apriEsecuzione(data)" />
              <Button icon="pi pi-download" class="p-button-outlined p-button-secondary"
                v-tooltip.top="'Scarica il documento originale'" @click="scarica(data)" />
            </div>
          </template>
        </Column>

        <template #expansion="{ data }">
          <div class="ec-storico">
            <div class="ec-storico-titolo">Storico dei controlli su questo documento</div>

            <div v-if="storicoInCorso[data.id]" class="ec-empty">Lettura dello storico...</div>
            <div v-else-if="!(storico[data.id] || []).length" class="ec-empty">
              Nessun controllo ancora eseguito su questo documento
            </div>

            <DataTable v-else :value="storico[data.id]" responsiveLayout="scroll" class="p-datatable-sm">
              <Column header="Eseguito il" style="width:11rem" bodyClass="ec-nowrap">
                <template #body="{ data: e }">{{ formatDateTime(e.eseguita_il) }}</template>
              </Column>
              <Column field="utente" header="Da" style="width:11rem" bodyClass="ec-nowrap">
                <template #body="{ data: e }">{{ e.utente || 'utente ignoto' }}</template>
              </Column>
              <Column header="Periodo" style="width:12rem" bodyClass="ec-nowrap">
                <template #body="{ data: e }">
                  <template v-if="e.periodo_dal">{{ formatDate(e.periodo_dal) }} - {{ formatDate(e.periodo_al) }}</template>
                  <span v-else class="text-500">-</span>
                </template>
              </Column>
              <!-- Unica colonna elastica dello storico: assorbe lo spazio residuo e
                   tronca, invece di allargare la tabella oltre il riquadro. -->
              <Column header="Esito" bodyClass="ec-desc-cell">
                <template #body="{ data: e }">
                  <div class="ec-esiti">
                    <span class="ec-pill" :class="pillClasse(e)">{{ pillTesto(e) }}</span>
                    <span v-if="e.stato === 'ok' && !e.quadratura_ok" class="ec-pill ec-pill-warn">PDF non quadra</span>
                  </div>
                  <span v-if="e.stato === 'ok'" class="ec-sub ec-desc">
                    {{ e.banca_righe }} righe in banca · {{ e.ask_righe }} in ask · {{ e.abbinati }} abbinate
                  </span>
                  <span v-else class="ec-sub ec-desc" :title="e.errore">{{ e.errore }}</span>
                </template>
              </Column>
              <Column header="Durata" style="width:6rem" bodyClass="text-right ec-nowrap" headerClass="ec-th-right">
                <template #body="{ data: e }"><span class="ec-sub">{{ formatDurata(e.durata_ms) }}</span></template>
              </Column>
              <Column header="" style="width:10rem" bodyClass="ec-nowrap">
                <template #body="{ data: e }">
                  <Button v-if="e.stato === 'ok' && e.esito_disponibile" label="Vedi esito" icon="pi pi-eye"
                    class="p-button-sm p-button-text" @click="apriEsitoStorico(e)" />
                  <span v-else-if="e.stato === 'ok'" class="ec-sub"
                    v-tooltip.top="'Il dettaglio delle differenze non e piu conservato. Rilancia il controllo per rivederlo.'">
                    dettaglio non conservato
                  </span>
                </template>
              </Column>
            </DataTable>
          </div>
        </template>
      </DataTable>
    </div>

    <!-- LANCIO DEL CONTROLLO -->
    <Dialog v-model:visible="esecuzioneDialog" :style="{ width: '480px' }" header="Esegui il controllo"
      :modal="true" class="p-fluid">
      <div v-if="documentoScelto" class="formgrid grid">
        <div class="field col-12">
          <div>Banca: <b>{{ documentoScelto.banca }}</b></div>
          <div class="ec-sub mt-1">{{ documentoScelto.nome_file }}</div>
        </div>
        <div class="field col-12">
          <label>Periodo <span class="text-500 font-normal">(facoltativo)</span></label>
          <Calendar v-model="periodo" dateFormat="dd/mm/yy" selectionMode="range" :manualInput="false"
            placeholder="Se vuoto, viene rilevato dal PDF" showButtonBar />
          <small class="text-500">
            L'estratto puo coprire piu mesi o un intero trimestre: lascia vuoto e il periodo viene letto dal file.
          </small>
        </div>
        <div v-if="documentoScelto.esecuzioni" class="field col-12">
          <div class="ec-nota">
            Questo documento e gia stato controllato {{ documentoScelto.esecuzioni }}
            {{ documentoScelto.esecuzioni === 1 ? 'volta' : 'volte' }}. La nuova esecuzione si affianca alle
            precedenti, che restano consultabili.
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text" :disabled="inEsecuzione"
          @click="esecuzioneDialog = false" />
        <Button label="Esegui" icon="pi pi-play" class="p-button-success" :loading="inEsecuzione"
          @click="esegui" />
      </template>
    </Dialog>

    <!-- ESITO DEL CONFRONTO -->
    <Dialog v-model:visible="esitoDialog" :style="{ width: '92%', maxWidth: '1400px' }"
      :maximizable="true" :modal="true">
      <template #header>
        <div class="flex align-items-center justify-content-between w-full pr-3">
          <span class="p-dialog-title">Confronto estratto conto banca</span>
          <div class="flex ec-spazio">
            <Button label="Controlla i pagamenti" icon="pi pi-euro" class="p-button-sm p-button-outlined"
              v-tooltip.top="'Verifica i pagamenti associati ai movimenti di questo periodo'"
              :disabled="!contoDellEsito" @click="vaiAlControlloPagamenti" />
            <Button label="Esporta in Excel" icon="pi pi-file-excel" class="p-button-sm p-button-success"
              :disabled="!esito" @click="esporta" />
          </div>
        </div>
      </template>

      <div v-if="esito" class="ec-check">

        <div class="flex align-items-center flex-wrap ec-spazio mb-3">
          <span class="text-2xl font-bold">{{ esito.conto }}</span>
          <span class="text-500">·</span>
          <span class="text-lg text-600">{{ formatDate(esito.periodo.dal) }} - {{ formatDate(esito.periodo.al) }}</span>
          <span v-if="esito.periodo.rilevato" class="ec-pill ec-pill-info ml-2">periodo rilevato dal PDF</span>
          <span v-if="esito.storico" class="ec-pill ec-pill-info ml-1">
            controllo del {{ formatDateTime(esito.esecuzione.eseguita_il) }} · {{ esito.esecuzione.utente }}
          </span>
        </div>

        <div v-if="!esito.attendibile" class="ec-banner ec-banner-warn mb-3">
          <div class="ec-banner-title">Lettura del PDF non attendibile</div>
          <div class="ec-banner-sub">
            I movimenti letti non tornano con i saldi dichiarati dalla banca
            (scarto {{ fmtEur(esito.quadratura.scarto) }}): il confronto qui sotto potrebbe
            mostrare differenze inesistenti. Segnalalo prima di intervenire sui movimenti.
          </div>
        </div>

        <div class="ec-banner mb-4" :class="esitoOk ? 'ec-banner-ok' : 'ec-banner-warn'">
          <div class="ec-banner-title">
            {{ esitoOk ? 'Nessuna differenza' : totDifferenze + (totDifferenze === 1 ? ' differenza da verificare' : ' differenze da verificare') }}
          </div>
          <div class="ec-banner-sub">
            {{ esitoOk
              ? 'Tutti i ' + esito.totali.abbinati + ' movimenti della banca risultano registrati in ask'
              : esito.totali.solo_banca_righe + ' presenti solo in banca · ' + esito.totali.solo_ask_righe + ' presenti solo in ask' }}
          </div>
        </div>

        <div class="grid mb-4">
          <div class="col-12 lg:col-6">
            <div class="ec-box h-full">
              <div class="ec-box-label">Movimenti confrontati</div>
              <div class="flex">
                <div class="ec-stat"><span class="ec-stat-num">{{ esito.totali.banca_righe }}</span><span class="ec-stat-lbl">Banca (PDF)</span></div>
                <div class="ec-stat"><span class="ec-stat-num">{{ esito.totali.ask_righe }}</span><span class="ec-stat-lbl">ask</span></div>
                <div class="ec-stat"><span class="ec-stat-num text-green-600">{{ esito.totali.abbinati }}</span><span class="ec-stat-lbl">Abbinati</span></div>
              </div>
            </div>
          </div>
          <div class="col-12 lg:col-6">
            <div class="ec-box h-full">
              <div class="ec-box-label">Totali di periodo</div>
              <div class="flex">
                <div class="ec-stat"><span class="ec-stat-num">{{ fmtEur(esito.totali.banca_totale) }}</span><span class="ec-stat-lbl">Banca (PDF)</span></div>
                <div class="ec-stat"><span class="ec-stat-num">{{ fmtEur(esito.totali.ask_totale) }}</span><span class="ec-stat-lbl">ask</span></div>
                <div class="ec-stat">
                  <span class="ec-stat-num" :class="Number(esito.totali.differenza) === 0 ? 'text-green-600' : 'text-red-600'">
                    {{ fmtEur(esito.totali.differenza) }}
                  </span>
                  <span class="ec-stat-lbl">Differenza</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="esito.troncato" class="ec-note mb-3">
          Elenchi limitati alle prime {{ esito.max_dettaglio }} righe per lato. Conteggi e totali restano completi
        </div>

        <div class="grid">
          <div class="col-12">
            <div class="ec-panel">
              <div class="ec-panel-head ec-head-banca">
                <span>Presenti in banca, non in ask</span>
                <span class="ec-count">
                  {{ esito.totali.solo_banca_righe }} {{ esito.totali.solo_banca_righe === 1 ? 'riga' : 'righe' }}
                  <template v-if="esito.totali.solo_banca_righe"> · {{ fmtEur(esito.totali.solo_banca_totale) }}</template>
                </span>
              </div>
              <div v-if="!esito.solo_banca.length" class="ec-empty">Nessuna differenza su questo lato</div>
              <DataTable v-else :value="esito.solo_banca" responsiveLayout="scroll"
                :paginator="esito.solo_banca.length > 8" :rows="8" :rowsPerPageOptions="[8, 25, 50, 100]"
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Visualizzate {first}-{last} di {totalRecords}" class="p-datatable-sm">
                <Column field="data_contabile" header="Data" bodyClass="ec-nowrap" style="width:7rem">
                  <template #body="{ data }">{{ formatDate(data.data_contabile) }}</template>
                </Column>
                <Column field="importo" header="Importo" bodyClass="text-right ec-nowrap" headerClass="ec-th-right" style="width:9rem">
                  <template #body="{ data }">
                    <span class="font-semibold" :class="{ 'text-red-600': data.importo < 0 }">{{ fmtEur(data.importo) }}</span>
                  </template>
                </Column>
                <Column field="descrizione" header="Descrizione" bodyClass="ec-desc-cell">
                  <template #body="{ data }"><span class="ec-desc" :title="data.descrizione">{{ data.descrizione }}</span></template>
                </Column>
                <Column header="" style="width:9rem" bodyClass="ec-nowrap">
                  <template #body="{ data }">
                    <span v-if="creati[chiaveRiga(data)]" class="ec-pill ec-pill-ok"
                      v-tooltip.top="'Movimento creato in ask'">
                      creato #{{ creati[chiaveRiga(data)] }}
                    </span>
                    <Button v-else-if="!esito.storico" label="Crea in ask" class="p-button-sm p-button-text"
                      v-tooltip.top="'Crea il movimento mancante in ask'"
                      @click="apriCreaMovimento(data)" />
                    <span v-else class="ec-sub"
                      v-tooltip.top="'Riesegui il controllo per creare i movimenti: questo risultato e di un\'esecuzione precedente.'">
                      solo consultazione
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>

          <div class="col-12">
            <div class="ec-panel">
              <div class="ec-panel-head ec-head-ask">
                <span>Presenti in ask, non in banca</span>
                <span class="ec-count">
                  {{ esito.totali.solo_ask_righe }} {{ esito.totali.solo_ask_righe === 1 ? 'riga' : 'righe' }}
                  <template v-if="esito.totali.solo_ask_righe"> · {{ fmtEur(esito.totali.solo_ask_totale) }}</template>
                </span>
              </div>
              <div v-if="!esito.solo_ask.length" class="ec-empty">Nessuna differenza su questo lato</div>
              <DataTable v-else :value="esito.solo_ask" responsiveLayout="scroll"
                :paginator="esito.solo_ask.length > 8" :rows="8" :rowsPerPageOptions="[8, 25, 50, 100]"
                paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                currentPageReportTemplate="Visualizzate {first}-{last} di {totalRecords}" class="p-datatable-sm">
                <Column field="data_contabile" header="Data" bodyClass="ec-nowrap" style="width:7rem">
                  <template #body="{ data }">{{ formatDate(data.data_contabile) }}</template>
                </Column>
                <Column field="importo" header="Importo" bodyClass="text-right ec-nowrap" headerClass="ec-th-right" style="width:9rem">
                  <template #body="{ data }">
                    <span class="font-semibold" :class="{ 'text-red-600': data.importo < 0 }">{{ fmtEur(data.importo) }}</span>
                  </template>
                </Column>
                <Column field="contabilizzato" header="Stato" bodyClass="ec-nowrap" style="width:8rem">
                  <template #body="{ data }">
                    <span :class="data.contabilizzato ? 'text-green-600' : 'text-500'">
                      {{ data.contabilizzato ? 'contabilizzato' : 'da contabilizzare' }}
                    </span>
                  </template>
                </Column>
                <Column field="descrizione" header="Descrizione" bodyClass="ec-desc-cell">
                  <template #body="{ data }"><span class="ec-desc" :title="data.descrizione">{{ data.descrizione }}</span></template>
                </Column>
                <Column header="" style="width:4rem" bodyClass="ec-nowrap">
                  <template #body="{ data }">
                    <Button icon="pi pi-external-link" class="p-button-sm p-button-text"
                      v-tooltip.top="'Apri il movimento in Estratti Conto'"
                      @click="apriMovimentoAsk(data)" />
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="esitoDialog = false" />
      </template>
    </Dialog>

    <!-- CREAZIONE DEL MOVIMENTO MANCANTE IN ASK -->
    <Dialog v-model:visible="creaDialog" :style="{ width: '560px' }" header="Crea il movimento in ask"
      :modal="true" class="p-fluid ec-crea" @hide="resetCrea">

      <div v-if="rigaDaCreare" class="ec-crea-riepilogo">
        <div><b>{{ esito.conto }}</b> · {{ formatDate(rigaDaCreare.data_contabile) }} · <b>{{ fmtEur(rigaDaCreare.importo) }}</b></div>
        <span class="ec-sub">{{ rigaDaCreare.descrizione }}</span>
      </div>

      <!-- Movimenti simili: si mostrano, non bloccano. Movimenti identici legittimi esistono. -->
      <div v-if="simili" class="ec-simili">
        <div class="ec-simili-titolo">
          {{ simili.totale === 1 ? 'C\'e gia un movimento' : 'Ci sono gia ' + simili.totale + ' movimenti' }}
          con questo importo, a pochi giorni di distanza
        </div>
        <div v-for="m in simili.righe" :key="m.id" class="ec-simili-riga">
          <span class="ec-nowrap">{{ formatDate(m.data_contabile) }}</span>
          <span class="ec-nowrap">{{ fmtEur(m.importo) }}</span>
          <span class="ec-desc" :title="m.descrizione">{{ m.descrizione }}</span>
          <span class="ec-sub ec-nowrap">{{ m.contabilizzato ? 'contabilizzato' : 'da contabilizzare' }}</span>
        </div>
        <div v-if="simili.totale > simili.righe.length" class="ec-sub">
          e altri {{ simili.totale - simili.righe.length }}
        </div>
        <div class="ec-simili-nota">
          Controlla che non sia lo stesso movimento. Se e davvero un'altra operazione, procedi.
        </div>
      </div>

      <div class="ec-nota-crea">
        Viene creata solo la riga di estratto conto, come <b>da contabilizzare</b> e senza tipo di
        pagamento, come fa l'import quando la causale non e riconosciuta. Il tipo si imposta dalla
        pagina Estratti Conto.
      </div>

      <template #footer>
        <Button label="Annulla" class="p-button-text p-button-secondary" :disabled="inCreazione"
          @click="creaDialog = false" />
        <Button :label="simili ? 'Crea comunque' : 'Crea'" icon="pi pi-check"
          :class="simili ? 'p-button-warning' : 'p-button-success'" :loading="inCreazione"
          @click="confermaCreaMovimento" />
      </template>
    </Dialog>

    <!-- REGISTRO ATTIVITA -->
    <Dialog v-model:visible="registroDialog" :style="{ width: 'auto' }"
      header="Registro attività" :modal="true" :maximizable="true" class="ec-registro">
      <div class="ec-sub mb-3">
        Chi ha caricato, eseguito o scaricato, in ordine dal piu recente.
      </div>
      <DataTable :value="registro" responsiveLayout="scroll" class="p-datatable-sm"
        :paginator="registro.length > 15" :rows="15" :rowsPerPageOptions="[15, 25, 50, 100]"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Visualizzate {first}-{last} di {totalRecords}">
        <template #empty><div class="ec-empty">Nessuna attivita registrata</div></template>
        <Column header="Quando">
          <template #body="{ data }">{{ formatDateTime(data.quando) }}</template>
        </Column>
        <Column field="utente" header="Chi">
          <template #body="{ data }">{{ data.utente || 'utente ignoto' }}</template>
        </Column>
        <Column header="Azione">
          <template #body="{ data }">
            <span class="ec-pill" :class="'ec-pill-' + data.azione">{{ etichettaAzione(data.azione) }}</span>
          </template>
        </Column>
        <Column field="id_file" header="Doc.">
          <template #body="{ data }"><span class="ec-sub">#{{ data.id_file || '-' }}</span></template>
        </Column>
        <Column header="Dettagli">
          <template #body="{ data }">{{ dettagli(data) }}</template>
        </Column>
      </DataTable>
      <template #footer>
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text" @click="registroDialog = false" />
      </template>
    </Dialog>

  </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import { useToast } from 'primevue/usetoast'
import moment from 'moment'
import * as XLSX from 'xlsx'
import Service from '@/services/ask-pratiche/pratiche-service'

export default {
  name: 'ControlliEstrattoBanca',

  setup() {
    const self = getCurrentInstance().appContext.config.globalProperties
    const service = new Service(self.$http, self.$eventBus)
    const toast = useToast()

    const documenti = ref([])
    const conti = ref([])
    const contoFiltro = ref(null)
    const caricamento = ref(false)

    const righeAperte = ref([])
    const storico = ref({})
    const storicoInCorso = ref({})

    const esecuzioneDialog = ref(false)
    const documentoScelto = ref(null)
    const periodo = ref(null)
    const inEsecuzione = ref(false)

    const esitoDialog = ref(false)
    const esito = ref(null)

    const registroDialog = ref(false)
    const registro = ref([])

    // ── Creazione del movimento mancante in ask ────────────────────────
    const creaDialog = ref(false)
    const rigaDaCreare = ref(null)
    const simili = ref(null)
    const inCreazione = ref(false)
    // Righe gia' create in questa schermata: l'esito a video e' una fotografia, e
    // dopo la creazione resterebbe uguale. Cosi' si vede subito cosa si e' fatto.
    const creati = ref({})

    const chiaveRiga = (riga) => `${riga.pagina}:${riga.riga_pdf}`

    // Un clic crea. Il dialog si apre solo se il server segnala movimenti simili:
    // e' li' che serve un occhio umano, non sulla creazione in se'.
    const apriCreaMovimento = (riga) => {
      rigaDaCreare.value = riga
      simili.value = null
      creaMovimento()
    }

    const resetCrea = () => {
      rigaDaCreare.value = null
      simili.value = null
    }

    const confermaCreaMovimento = () => creaMovimento()

    const creaMovimento = () => {
      inCreazione.value = true
      service.creaMovimentoEstrattoBanca({
        idFile: esito.value.file.id,
        idEsecuzione: esito.value.esecuzione_id,
        conferma: simili.value !== null,
        riga: rigaDaCreare.value,
      })
        .then((res) => {
          // Il server risponde cosi' quando ci sono movimenti simili: non ha creato nulla,
          // aspetta che l'operatore guardi e confermi.
          if (res.conferma_richiesta) {
            simili.value = res.simili
            creaDialog.value = true
            return
          }

          creati.value = { ...creati.value, [chiaveRiga(rigaDaCreare.value)]: res.movimento.id }
          creaDialog.value = false
          toast.add({
            severity: 'success', summary: 'Movimento creato',
            detail: `Creato in ask come da contabilizzare (#${res.movimento.id}).`,
            life: 7000
          })
        })
        .catch((error) => mostraErrore(error, 'Creazione non riuscita'))
        .finally(() => { inCreazione.value = false })
    }

    // Il registro attivita' lo vede solo l'admin, cioe' l'unico profilo con `all = 1`
    // (stesso criterio del router e del menu). Il try serve perche' senza il profilo
    // in localStorage il render andrebbe in errore.
    const isAdmin = computed(() => {
      try {
        return Number(JSON.parse(atob(localStorage.getItem('user'))).user[0].profile.all) === 1
      } catch (e) {
        return false
      }
    })

    const contiFiltro = computed(() => [{ id: null, nome: 'Tutti i conti' }, ...conti.value])

    const totDifferenze = computed(() => {
      if (!esito.value) return 0
      return esito.value.totali.solo_banca_righe + esito.value.totali.solo_ask_righe
    })
    const esitoOk = computed(() => totDifferenze.value === 0)

    // ── Lettura ────────────────────────────────────────────────────────

    const caricaDocumenti = async () => {
      caricamento.value = true
      try {
        const res = await service.getDocumentiEstrattoBanca(contoFiltro.value)
        documenti.value = res.documenti || []
        conti.value = res.conti || []
        // Lo storico gia aperto va riletto: potrebbe essere cambiato.
        storico.value = {}
        righeAperte.value = []
      } finally {
        caricamento.value = false
      }
    }

    const caricaStorico = async (idFile) => {
      storicoInCorso.value = { ...storicoInCorso.value, [idFile]: true }
      try {
        const res = await service.getEsecuzioniEstrattoBanca({ idFile })
        storico.value = { ...storico.value, [idFile]: res.esecuzioni || [] }
      } finally {
        storicoInCorso.value = { ...storicoInCorso.value, [idFile]: false }
      }
    }

    const onRowExpand = (evt) => caricaStorico(evt.data.id)

    // ── Azioni ─────────────────────────────────────────────────────────

    const apriEsecuzione = (documento) => {
      documentoScelto.value = documento
      periodo.value = null
      esecuzioneDialog.value = true
    }

    const esegui = async () => {
      if (!documentoScelto.value) return

      const idFile = documentoScelto.value.id
      let dates = null
      const r = periodo.value
      if (r && r.length === 2 && r[0] && r[1]) {
        dates = r.map(d => moment(d).format('YYYY-MM-DD')).join(',')
      }

      inEsecuzione.value = true
      try {
        const res = await service.eseguiControlloEstrattoBanca(idFile, dates)
        esito.value = res
        esecuzioneDialog.value = false
        esitoDialog.value = true
        await caricaDocumenti()
      } catch (error) {
        mostraErrore(error, 'Errore durante il controllo')
      } finally {
        inEsecuzione.value = false
      }
    }

    const apriEsitoStorico = async (esecuzione) => {
      try {
        esito.value = await service.getEsitoEstrattoBanca(esecuzione.id)
        esitoDialog.value = true
      } catch (error) {
        mostraErrore(error, 'Esito non consultabile')
      }
    }

    const scarica = (documento) => service.scaricaEstrattoBanca(documento.id)

    // I due controlli si incatenano: prima che ask contenga tutto quello che la banca
    // ha registrato, poi che quei movimenti abbiano i pagamenti giusti. Stesso conto,
    // stesso periodo, gia' compilati.
    const vaiAlControlloPagamenti = () => {
      const p = esito.value.periodo
      window.open(`/controllo-pagamenti?conto=${contoDellEsito.value}&dal=${p.dal}&al=${p.al}`, '_blank')
    }

    const contoDellEsito = computed(() => (esito.value ? esito.value.cod_type_pay_mode || null : null))

    const apriMovimentoAsk = (movimento) => {
      const rotta = movimento.contabilizzato ? 'regularized' : 'not-regularized'
      window.open(`/estratti-conto/detail/${rotta}/${movimento.id}`, '_blank')
    }

    const apriRegistro = async () => {
      registroDialog.value = true
      const res = await service.getAuditEstrattoBanca(
        contoFiltro.value ? { codTypePayMode: contoFiltro.value } : {}
      )
      registro.value = res.movimenti || []
    }

    const mostraErrore = (error, titolo) => {
      const status = error?.response?.status
      const messaggi = error?.response?.data
      if (status === 422 && typeof messaggi === 'object') {
        Object.keys(messaggi).forEach(chiave => {
          messaggi[chiave].forEach(m =>
            toast.add({ severity: 'warn', summary: 'Attenzione', detail: m, life: 10000 }))
        })
        return
      }
      const detail = messaggi?.message || messaggi?.error || error?.message || 'Errore imprevisto'
      toast.add({ severity: 'error', summary: titolo, detail, life: 10000 })
    }

    /**
     * Esporta l'esito in un file Excel con tre fogli: il riepilogo (utile per
     * allegarlo a una segnalazione) e i due elenchi di differenze.
     */
    const esporta = () => {
      if (!esito.value) return

      const r = esito.value
      const t = r.totali
      const wb = XLSX.utils.book_new()

      const riepilogo = [
        { Voce: 'Conto', Valore: r.conto },
        { Voce: 'Periodo dal', Valore: r.periodo.dal },
        { Voce: 'Periodo al', Valore: r.periodo.al },
        { Voce: 'Periodo rilevato dal PDF', Valore: r.periodo.rilevato ? 'si' : 'no' },
        { Voce: 'File', Valore: r.file.nome },
        { Voce: 'Movimenti banca (PDF)', Valore: t.banca_righe },
        { Voce: 'Movimenti ask', Valore: t.ask_righe },
        { Voce: 'Abbinati', Valore: t.abbinati },
        { Voce: 'Presenti solo in banca', Valore: t.solo_banca_righe },
        { Voce: 'Presenti solo in ask', Valore: t.solo_ask_righe },
        { Voce: 'Totale banca', Valore: t.banca_totale },
        { Voce: 'Totale ask', Valore: t.ask_totale },
        { Voce: 'Differenza', Valore: t.differenza },
        { Voce: 'Quadratura del PDF', Valore: r.attendibile ? 'corretta' : 'NON quadra' },
        { Voce: 'Scarto quadratura', Valore: r.quadratura ? r.quadratura.scarto : null },
        { Voce: 'Controllo eseguito il', Valore: r.controllato_il },
        { Voce: 'Eseguito da', Valore: r.esecuzione ? r.esecuzione.utente : null },
      ]

      const soloBanca = r.solo_banca.map(m => ({
        'Data': m.data_contabile,
        'Valuta': m.data_valuta,
        'Importo': m.importo,
        'Descrizione': m.descrizione,
        'Pagina PDF': m.pagina,
      }))

      const soloAsk = r.solo_ask.map(m => ({
        'Data': m.data_contabile,
        'Valuta': m.data_valuta,
        'Importo': m.importo,
        'Stato': m.contabilizzato ? 'contabilizzato' : 'da contabilizzare',
        'Descrizione': m.descrizione,
      }))

      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(riepilogo), 'Riepilogo')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(soloBanca.length ? soloBanca : [{ Esito: 'Nessuna differenza' }]), 'Solo in banca')
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(soloAsk.length ? soloAsk : [{ Esito: 'Nessuna differenza' }]), 'Solo in ask')

      XLSX.writeFile(wb, `Confronto_${r.conto}_${r.periodo.dal}_${r.periodo.al}.xlsx`.replace(/\s+/g, '_'))

      if (r.troncato) {
        toast.add({
          severity: 'warn', summary: 'Attenzione',
          detail: `Gli elenchi sono limitati alle prime ${r.max_dettaglio} righe per lato, quindi anche il file esportato lo e.`,
          life: 8000
        })
      }
    }

    // ── Formattazione ──────────────────────────────────────────────────

    const formatDate = (v) => (v ? moment(v).format('DD/MM/YYYY') : '-')
    const formatDateTime = (v) => (v ? moment(v).format('DD/MM/YYYY HH:mm') : '-')

    const formatBytes = (b) => {
      if (!b) return '-'
      if (b < 1024) return b + ' B'
      if (b < 1048576) return (b / 1024).toFixed(0) + ' KB'
      return (b / 1048576).toFixed(1) + ' MB'
    }

    const formatDurata = (ms) => {
      if (!ms && ms !== 0) return '-'
      return ms < 1000 ? ms + ' ms' : (ms / 1000).toFixed(1) + ' s'
    }

    const fmtEur = (v) => {
      if (v === null || v === undefined || v === '') return '-'
      const n = Number(v)
      if (isNaN(n)) return '-'
      return n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
    }

    const pillClasse = (e) => {
      if (!e || e.stato !== 'ok') return 'ec-pill-errore'
      return e.differenze === 0 || (e.solo_banca_righe === 0 && e.solo_ask_righe === 0)
        ? 'ec-pill-ok' : 'ec-pill-warn'
    }

    const pillTesto = (e) => {
      if (!e) return '-'
      if (e.stato !== 'ok') return 'Errore'
      const n = e.differenze !== null && e.differenze !== undefined
        ? e.differenze
        : (e.solo_banca_righe || 0) + (e.solo_ask_righe || 0)
      return n === 0 ? 'Nessuna differenza' : n + (n === 1 ? ' differenza' : ' differenze')
    }

    const etichettaAzione = (a) => ({
      caricamento: 'Caricamento',
      esecuzione: 'Esecuzione',
      download: 'Download',
      errore: 'Errore',
    }[a] || a)

    const dettagli = (riga) => {
      const d = riga.dettagli
      if (!d) return ''
      if (riga.azione === 'caricamento') return d.nome_file + (d.duplicato ? ' (gia presente come #' + d.duplicato + ')' : '')
      if (riga.azione === 'download') return d.nome_file || ''
      if (riga.azione === 'errore') return d.errore || ''
      if (riga.azione === 'esecuzione') {
        const p = d.periodo ? `${d.periodo.dal} - ${d.periodo.al}` : ''
        const diff = d.differenze === 0 ? 'nessuna differenza' : d.differenze + ' differenze'
        return [p, diff, d.quadra === false ? 'PDF non quadra' : null].filter(Boolean).join(' · ')
      }
      return JSON.stringify(d)
    }

    onMounted(caricaDocumenti)

    return {
      documenti, conti, contiFiltro, contoFiltro, caricamento, caricaDocumenti,
      righeAperte, storico, storicoInCorso, onRowExpand,
      esecuzioneDialog, documentoScelto, periodo, inEsecuzione, apriEsecuzione, esegui,
      esitoDialog, esito, esitoOk, totDifferenze, apriEsitoStorico, esporta,
      registroDialog, registro, apriRegistro, dettagli, etichettaAzione, isAdmin,
      creaDialog, rigaDaCreare, simili, inCreazione, creati, chiaveRiga,
      apriCreaMovimento, confermaCreaMovimento, resetCrea,
      scarica, apriMovimentoAsk, vaiAlControlloPagamenti, contoDellEsito, formatDate, formatDateTime, formatBytes, formatDurata, fmtEur,
      pillClasse, pillTesto,
    }
  },
}
</script>

<style>
/* PrimeFlex 3.0.0 non ha ancora le utility gap-*. */
.ec-spazio { gap: .5rem; }
.ec-spazio-l { gap: 1rem; }

.ec-controlli .ec-comandi {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: .75rem;
}

.ec-controlli .ec-filtro {
  min-width: 13rem;
}

.ec-controlli .ec-tabella .p-datatable-thead > tr > th {
  padding: .9rem 1.1rem;
  white-space: nowrap;
}

.ec-controlli .ec-tabella .p-datatable-tbody > tr > td {
  padding: 1.1rem;
  vertical-align: middle;
}

.ec-controlli .ec-azioni {
  display: flex;
  justify-content: flex-end;
  gap: .6rem;
}

.ec-controlli .ec-esiti {
  display: flex;
  flex-wrap: wrap;
  gap: .35rem;
  margin-bottom: .3rem;
}

.ec-controlli .ec-th-center .p-column-header-content {
  justify-content: center;
}

.ec-controlli .ec-file {
  display: block;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ec-sub {
  display: block;
  font-size: .75rem;
  color: #6d6d6d;
}

.ec-count {
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.ec-nota {
  background: #f5f8fd;
  color: #46709e;
  border-radius: 6px;
  padding: .6rem .8rem;
  font-size: .8rem;
}

.ec-controlli .ec-storico {
  padding: .75rem 1.25rem 1.25rem 2rem;
  background: #fbfbfc;
}

.ec-controlli .ec-storico .p-datatable-thead > tr > th,
.ec-controlli .ec-storico .p-datatable-tbody > tr > td {
  padding: .65rem .85rem;
}

.ec-controlli .ec-storico .p-datatable-thead > tr > th {
  white-space: nowrap;
}

.ec-controlli .ec-storico .p-button-label {
  white-space: nowrap;
}


.ec-controlli .ec-storico-titolo {
  font-size: .7rem;
  letter-spacing: .06em;
  text-transform: uppercase;
  font-weight: 600;
  color: #6d6d6d;
  margin-bottom: .6rem;
}

.ec-pill {
  display: inline-flex;
  align-items: center;
  font-size: .78rem;
  font-weight: 600;
  padding: .25rem .7rem;
  border-radius: 999px;
  white-space: nowrap;
}

.ec-pill-ok,
.ec-pill-caricamento {
  background: #f4fbf6;
  color: #3a7a55;
}

.ec-pill-warn,
.ec-pill-esecuzione {
  background: #fefaf0;
  color: #7d6526;
}

.ec-pill-errore {
  background: #fdf5f5;
  color: #9c4b4b;
}

.ec-pill-info,
.ec-pill-download {
  background: #f1f6fd;
  color: #3d6fa8;
}

.ec-empty {
  padding: 2rem 1rem;
  text-align: center;
  color: #6d6d6d;
  font-size: .9rem;
}

.ec-check .ec-banner {
  padding: .9rem 1.25rem;
  border-radius: 8px;
  border-left: 5px solid;
}

.ec-check .ec-banner-title {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
}

.ec-check .ec-banner-sub {
  font-size: .875rem;
  opacity: .8;
  margin-top: .15rem;
}

.ec-check .ec-banner-ok {
  background: #f4fbf6;
  border-color: #8fcfa8;
  color: #3a7a55;
}

.ec-check .ec-banner-warn {
  background: #fefaf0;
  border-color: #eccf85;
  color: #7d6526;
}

.ec-check .ec-box {
  border: 1px solid rgba(51, 51, 51, .08);
  border-radius: 8px;
  padding: .9rem 1.1rem;
}

.ec-check .ec-box-label {
  font-size: .7rem;
  letter-spacing: .06em;
  text-transform: uppercase;
  font-weight: 600;
  color: #6d6d6d;
  margin-bottom: .6rem;
}

.ec-check .ec-stat {
  flex: 1 1 0;
  min-width: 0;
  padding-right: .75rem;
}

.ec-check .ec-stat+.ec-stat {
  border-left: 1px solid rgba(51, 51, 51, .07);
  padding-left: .75rem;
}

.ec-check .ec-stat-num {
  display: block;
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ec-check .ec-stat-lbl {
  display: block;
  font-size: .75rem;
  color: #6d6d6d;
  margin-top: .1rem;
}

.ec-check .ec-note {
  font-size: .8rem;
  color: #6b6b6b;
  padding: .35rem 0;
}

.ec-check .ec-panel {
  border: 1px solid rgba(51, 51, 51, .08);
  border-radius: 8px;
  overflow: hidden;
  height: 100%;
}

.ec-check .ec-panel-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: .7rem 1rem;
  font-weight: 600;
  font-size: .9rem;
  border-bottom: 1px solid rgba(51, 51, 51, .07);
}

.ec-check .ec-head-banca {
  background: #f5f8fd;
  color: #46709e;
}

.ec-check .ec-head-ask {
  background: #f8f5fc;
  color: #6f5891;
}

.ec-registro {
  min-width: min(34rem, 95vw);
  max-width: 95vw;
}

.ec-registro .p-datatable-table {
  width: auto;
}

.ec-registro .p-datatable-thead > tr > th,
.ec-registro .p-datatable-tbody > tr > td {
  white-space: nowrap;
}

.ec-registro .p-dialog-content {
  overflow-x: auto;
}

/* ── Creazione del movimento mancante ─────────────────────────────── */
.ec-crea .ec-crea-riepilogo {
  padding: .8rem 1rem;
  border-radius: 8px;
  background: #f5f8fd;
  margin-bottom: 1.25rem;
}

.ec-crea .ec-simili {
  padding: .85rem 1rem;
  border-left: 4px solid #eccf85;
  border-radius: 6px;
  background: #fefaf0;
  margin-bottom: 1.25rem;
}

.ec-crea .ec-simili-titolo {
  font-weight: 600;
  font-size: .85rem;
  color: #7d6526;
  margin-bottom: .5rem;
}

.ec-crea .ec-simili-riga {
  display: grid;
  grid-template-columns: 6rem 6rem 1fr auto;
  gap: .6rem;
  align-items: center;
  font-size: .8rem;
  padding: .3rem 0;
  border-top: 1px solid rgba(125, 101, 38, .15);
}

.ec-crea .ec-simili-nota {
  font-size: .78rem;
  color: #7d6526;
  margin-top: .6rem;
}

.ec-crea .ec-nota-crea {
  font-size: .8rem;
  color: #6d6d6d;
  line-height: 1.5;
  padding-top: .5rem;
  border-top: 1px solid rgba(51, 51, 51, .08);
}

.ec-nowrap {
  white-space: nowrap;
}

.ec-desc-cell {
  width: 100%;
  max-width: 0;
}

.ec-desc {
  display: block;
  font-size: .8rem;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ec-th-right .p-column-header-content {
  justify-content: flex-end;
}

.ec-check .p-datatable .p-datatable-tbody>tr>td,
.ec-check .p-datatable .p-datatable-thead>tr>th {
  padding: .45rem .75rem;
}
</style>
